import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { TextField, Button, Container, Paper, Typography } from '@mui/material';
import { FieldArray, Form, Formik, FormikProps } from 'formik';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import { useAppContext } from '../../../utils/content.util';
import { onError } from '../../../utils/error.util';
import { Payment } from '../../../models/payment.model';
import { createPayment, getPayment, updatePayment } from '../../../services/payments.service';
import { CreatePayment } from '../../../models/createPayment.model';
import { paymentInitialValues, PaymentSchemaYup } from './validationSchema';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { isDev } from '../../../utils/utils';

export default function PaymentFormContainer() {
  const { isAuthenticated } = useAppContext();

  const navigate = useNavigate();

  const { paymentId } = useParams();
  const [payment, setPayment] = useState<Payment>(paymentInitialValues);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function onLoad() {
      if (!isAuthenticated || !paymentId) {
        setIsLoading(false);
        return;
      }

      if (paymentId === 'new') {
        // new payment
        setIsLoading(false);
        return;
      }

      try {
        const response = await getPayment(paymentId);

        setPayment({ ...response, dueDate: new Date(response.dueDate) });
      } catch (e) {
        onError(e);
      }

      setIsLoading(false);
    }

    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);

  const onSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      if (payment.id) {
        const payload: Payment = {
          ...payment,
          ...values,
          items: values.items.map((item: any) => ({
            ...item,
            price: Number(item.price),
          })),
        };

        await updatePayment(payload);
      } else {
        const { id, ...createPaymentData } = values;
        const payload: CreatePayment = {
          ...createPaymentData,
          price: calculateTotal(values.items),
          items: values.items.map((item: any) => ({
            ...item,
            price: Number(item.price),
          })),
        };

        await createPayment(payload);
      }
      setIsSubmitting(false);
      navigate('/payments');
    } catch (err) {
      onError(err);
      alert('Something wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateTotal = (items: Payment['items']): number => {
    return items.reduce((sum, item) => sum + Number(item.price), 0);
  };

  function renderPaymentItem() {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Formik
          initialValues={{ ...payment, price: calculateTotal(payment.items) }}
          validationSchema={PaymentSchemaYup}
          onSubmit={(values) => onSubmit(values)}
        >
          {({ handleChange, handleBlur, values, touched, errors, setFieldValue }: FormikProps<Payment>) => (
            <Form noValidate>
              <fieldset disabled={payment.status === 'paid'}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="firstName"
                      label="First Name"
                      variant="outlined"
                      fullWidth
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="lastName"
                      label="Last Name"
                      variant="outlined"
                      fullWidth
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                    />
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="email"
                      label="Email"
                      variant="outlined"
                      type="email"
                      fullWidth
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="phone"
                      label="Phone"
                      variant="outlined"
                      fullWidth
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                      slotProps={{
                        input: {
                          inputMode: 'numeric',
                          type: 'tel',
                          placeholder: '(XXX) XXX-XXXX',
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      name="description"
                      label="Description"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={4}
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                    />
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <DatePicker
                      label="Due Date"
                      value={values.dueDate ? dayjs(values.dueDate) : null}
                      onChange={(newValue) => setFieldValue('dueDate', newValue?.toDate())}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: touched.dueDate && Boolean(errors.dueDate),
                          helperText: touched.dueDate && String(errors.dueDate),
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="price"
                      label="Price"
                      variant="outlined"
                      type="number"
                      disabled
                      fullWidth
                      // value={values.price}
                      value={calculateTotal(values.items)}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.price && Boolean(errors.price)}
                      helperText={touched.price && errors.price}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Paper
                      elevation={0}
                      sx={
                        {
                          // border: '1px solid #e0e0e0',
                          // borderRadius: 1,
                          // p: 2,
                          // mb: 2,
                        }
                      }
                    >
                      <Typography variant="h5" sx={{ mb: 2 }}>
                        Services
                      </Typography>

                      <FieldArray
                        name="items"
                        render={(arrayHelpers) => (
                          <div>
                            {(values.items || []).map((item, index) => (
                              <div key={index}>
                                <Grid
                                  container
                                  spacing={2}
                                  sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    p: 2,
                                    mb: 2,
                                  }}
                                >
                                  <Grid size={{ xs: 6 }}>
                                    <TextField
                                      name={`items[${index}].name`}
                                      label="Name"
                                      variant="outlined"
                                      fullWidth
                                      value={item.name}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      // error={touched.items?.[index].title && Boolean(errors.items?.[index])}
                                      // helperText={touched.items && String(errors.items)}
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 6 }}>
                                    <TextField
                                      name={`items[${index}].price`}
                                      label="Price"
                                      variant="outlined"
                                      fullWidth
                                      value={item.price}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      // error={touched.items?.[index]?.price && Boolean(errors.items?.[index]?.price)}
                                      // helperText={touched.items && String(errors.items)}
                                    />
                                  </Grid>

                                  <Grid size={{ xs: 12 }}>
                                    <TextField
                                      name={`items[${index}].description`}
                                      label="Description"
                                      variant="outlined"
                                      multiline
                                      rows={4}
                                      fullWidth
                                      value={item.description}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      // error={touched.items?.[index]?.subTitle && Boolean(errors.items?.[index]?.subTitle)}
                                      // helperText={touched.items && String(errors.items)}
                                    />
                                  </Grid>

                                  {values.items.length > 1 && (
                                    <Button type="button" onClick={() => arrayHelpers.remove(index)}>
                                      Remove
                                    </Button>
                                  )}
                                </Grid>
                                {index === values.items.length - 1 && (
                                  <Button
                                    type="button"
                                    onClick={() => arrayHelpers.push({ title: '', subTitle: '', price: 0 })}
                                  >
                                    Add Service
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </fieldset>
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', gap: 1, my: 2 }}>
                <Button variant="contained" type="submit" disabled={isSubmitting} hidden={payment.status === 'paid'}>
                  Save
                </Button>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </Grid>

              {isDev ? (
                <>
                  <div>
                    <p>Values:</p>
                    <code>{JSON.stringify(values, null, 2)}</code>
                  </div>
                  <div>
                    <p>Errors:</p>
                    <code>{JSON.stringify(errors, null, 2)}</code>
                  </div>
                </>
              ) : null}
            </Form>
          )}
        </Formik>
      </Container>
    );
  }

  return (
    <>
      <div className="payment-items">
        <h2 className="pb-2 mt-4 mb-3 border-bottom">{paymentId === 'new' ? 'Add New Payment' : 'Edit Payment'}</h2>
      </div>
      {isLoading ? <CircularProgressComponent /> : renderPaymentItem()}
    </>
  );
}
